"use client";
import React, { useEffect, useState, useRef, useCallback } from "react";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { OffersOffer } from "@/services/dataTypes";
import { apiRandomOfferBanners } from "@/apis/offers";
import { filterBannersByDimensions, getBannerDimensions } from "@/constants/hooks";
import Banner from "./Banner";
import { faAngleLeft, faAngleRight, FontAwesomeIcon } from "@/constants/icons";
interface Props {
    companyId: string;
    mer_slug: string;
    slug_type: string;
    domain: string;
}
const HorizontalBannerSlider = ({ companyId, slug_type, mer_slug, domain }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [pagination, setPagination] = useState<{ current_page: number; last_page: number; next_page: number | null } | null>(null);
    const latestRequest = useRef(0);
    const MAX_EMPTY_ATTEMPTS = 1;
    const emptyAttempts = useRef(0);
    // 1. Ref and state for intersection observer
    const sliderSectionRef = useRef<HTMLElement | null>(null);
    const [sliderInView, setSliderInView] = useState(false);
    const [isSliderReady, setIsSliderReady] = useState(false);
    const autoplayRef = useRef<NodeJS.Timeout | null>(null);
    const offersRef = useRef(offers);
    const hasMoreRef = useRef(hasMore);
    const loadingRef = useRef(loading);
    const paginationRef = useRef(pagination);
    const sliderInViewRef = useRef(sliderInView);
    useEffect(() => { offersRef.current = offers; }, [offers]);
    useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
    useEffect(() => { loadingRef.current = loading; }, [loading]);
    useEffect(() => { paginationRef.current = pagination; }, [pagination]);
    useEffect(() => { sliderInViewRef.current = sliderInView; }, [sliderInView]);
    // Keen slider
    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        loop: true,
        slides: { perView: 1 },
        mode: 'snap',
        created: () => {
            setIsSliderReady(true);
        },
        slideChanged: (s) => {
            if (
                sliderInViewRef.current &&
                s.track.details.rel === offersRef.current.length - 1 &&
                hasMoreRef.current &&
                !loadingRef.current &&
                paginationRef.current?.next_page
            ) {
                getOffers(paginationRef.current.next_page);
            }
        },
    });
    const autoplayInterval = 4000;
    const autoplayDuration = 800;
    const startAutoplay = () => {
        if (!slider.current || !isSliderReady || autoplayRef.current) return;
        autoplayRef.current = setInterval(() => {
            const details = slider.current?.track.details;
            const currentAbsIndex = details?.abs || 0;
            slider.current?.moveToIdx(currentAbsIndex + 1, true, {
                duration: autoplayDuration,
                easing: (t) => 1 - Math.pow(1 - t, 3),
            });
        }, autoplayInterval);
    };
    const stopAutoplay = () => {
        if (autoplayRef.current) {
            clearInterval(autoplayRef.current);
            autoplayRef.current = null;
        }
    };
    useEffect(() => {
        if (!slider.current || !isSliderReady) return;
        startAutoplay();
        const wrapper = sliderSectionRef.current;
        if (wrapper) {
            wrapper.addEventListener('mouseenter', stopAutoplay);
            wrapper.addEventListener('mouseleave', startAutoplay);
        }
        return () => {
            stopAutoplay();
            if (wrapper) {
                wrapper.removeEventListener('mouseenter', stopAutoplay);
                wrapper.removeEventListener('mouseleave', startAutoplay);
            }
        };
    }, [slider, isSliderReady]);
    const getOffers = useCallback(async (pageNum: number, reset = false) => {
        setLoading(true);
        const requestId = Date.now();
        latestRequest.current = requestId;
        try {
            const bannerOffers = await apiRandomOfferBanners(companyId, pageNum);
            if (latestRequest.current !== requestId) return;
            const apiOffers = bannerOffers?.data?.offers || [];
            const filterOfferBanner = filterBannersByDimensions(apiOffers, 300, 1000, 0, 150);
            const paginationData = bannerOffers?.data?.pagination || {};
            setPagination({
                current_page: paginationData.current_page,
                last_page: paginationData.last_page,
                next_page: paginationData.next_page,
            });
            if (filterOfferBanner.length === 0 && paginationData.next_page && emptyAttempts.current < MAX_EMPTY_ATTEMPTS) {
                emptyAttempts.current += 1;
                await getOffers(paginationData.next_page, false);
                return;
            } else {
                emptyAttempts.current = 0;
            }
            if (reset) {
                setOffers(filterOfferBanner);
            } else if (filterOfferBanner.length > 0) {
                setOffers(prev => [...prev, ...filterOfferBanner]);
            }
            setHasMore(paginationData.next_page !== null);
        } finally {
            setLoading(false);
        }
    }, [companyId]);
    // 2. Set up intersection observer — triggers initial fetch on first entry,
    //    then keeps sliderInView in sync for pagination.
    const hasFetchedRef = useRef(false);
    useEffect(() => {
        // Reset fetch gate whenever companyId changes
        hasFetchedRef.current = false;

        if (!sliderSectionRef.current) return;
        const observer = new window.IntersectionObserver(
            (entries) => {
                const isIntersecting = entries[0].isIntersecting;
                setSliderInView(isIntersecting);
                // Fire the initial fetch only once, when the section first enters the viewport
                if (isIntersecting && !hasFetchedRef.current) {
                    hasFetchedRef.current = true;
                    setOffers([]);
                    setPagination(null);
                    emptyAttempts.current = 0;
                    getOffers(1, true);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sliderSectionRef.current);
        return () => observer.disconnect();
    }, [companyId, getOffers]);
    useEffect(() => {
        if (slider.current) {
            slider.current.update();
        }
    }, [offers]);
    // Keen slider navigation
    const goPrev = () => slider.current?.prev();
    const goNext = () => slider.current?.next();
    return (
        <section ref={sliderSectionRef} className="relative w-full bg-[#fff8f0] py-2 md:py-4">
            {offers?.length > 0 ? (
                <div className="relative">
                    <div ref={sliderRef} className="keen-slider">
                        {offers.map((items: OffersOffer, i: number) => {
                            const dimension = getBannerDimensions(items);
                            return (
                                <div key={i} className="keen-slider__slide flex h-[120px] items-center justify-center overflow-hidden">
                                    <Banner data={items} height={dimension?.height} width={dimension?.width} offerLink={null} mer_slug={mer_slug} slug_type={slug_type} domain={domain} plain />
                                </div>
                            );
                        })}
                        {loading && (
                            <div className="keen-slider__slide flex h-[120px] items-center justify-center">
                                <span className="text-sm font-medium text-slate-500">Loading more banners...</span>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={goPrev}
                        aria-label="Previous banner"
                        className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#9ca3af]/70 text-black transition hover:bg-[#ff912f] hover:text-white md:left-6"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={goNext}
                        aria-label="Next banner"
                        className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#9ca3af]/70 text-black transition hover:bg-[#ff912f] hover:text-white md:right-6"
                    >
                        <FontAwesomeIcon icon={faAngleRight} className="h-4 w-4" />
                    </button>
                </div>
            ) : loading ? (
                <div className="flex h-[120px] items-center justify-center">
                    <span className="text-sm font-medium text-slate-500">Loading banners...</span>
                </div>
            ) : null}
        </section>
    );
};
export default HorizontalBannerSlider;